import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  API_BASE,
  type ApiKey,
  type ApiKeyCreated,
  type AuditEntry,
  type Category,
  type InventoryLevel,
  type Movement,
  type Page,
  type OriginChannelKind,
  type PaymentMethod,
  type PaymentMethodType,
  type PaymentRejectReason,
  type PendingPayment,
  type PendingPaymentStatus,
  type PriceList,
  type PriceListItem,
  type ResolvedTenantConfig,
  type ShippingInfo,
  type Product,
  type ProductMode,
  type Supplier,
  type Tenant,
  type UpdateTenantSettingsInput,
  type UpdateTenantStorefrontPayload,
  type Warehouse,
  type DeliveryStatus,
  type WebhookDelivery as WebhookDeliveryType,
  type WebhookEndpoint as WebhookEndpointType,
  type WebhookEndpointCreated,
  type WebhookEventType,
  type AttributionReportResponse,
  type ConversionEvent as ConversionEventType,
  type ConversionEventStatus,
  type ConversionPlatform as ConversionPlatformType,
  type ConversionPlatformType as ConversionPlatformKind,
} from './api';

// ===== UI helpers =====

/**
 * Toast inline efímero compartido (4s default).
 * Reusar en lugar de duplicar la implementación en cada route.
 */
export type EphemeralToast = { kind: 'ok' | 'error'; msg: string } | null;
export function useEphemeralToast(durationMs = 4000) {
  const [toast, setToast] = useState<EphemeralToast>(null);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), durationMs);
    return () => window.clearTimeout(t);
  }, [toast, durationMs]);
  return [toast, setToast] as const;
}

// ===== Session =====

/**
 * D1-F2b: shape de GET /auth/me.
 *
 * `impersonation` solo viene cuando la sesión es de "modo soporte" — un
 * super-admin del SaaS entró al panel del comercio en solo lectura para
 * diagnosticar. En una sesión normal de comercio el campo NO está presente
 * (opcional, backward-compat). `superAdmin` es el email del super-admin.
 */
export interface MeResponse {
  tenant: Tenant;
  session: { email?: string; role?: 'owner' | 'admin' | 'member' };
  impersonation?: { superAdmin: string };
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    // 401 = sin sesión, no reintentes (sería ruido). 5xx/network = reintenta
    // 1 vez antes de declarar fallo: cubre flaps transitorios del API durante
    // un deploy. Layout discrimina el error y solo redirige a /login en 401.
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

/**
 * D2: funcionalidades habilitadas por tenant. El backend lo devuelve como
 * `Record<string, boolean>` en el response de GET /auth/tenant-settings;
 * tipamos las 5 keys conocidas (todas opcionales) para autocomplete +
 * protección contra typos en los gates del Layout. Una key en `false`
 * significa que el tenant NO tiene esa funcionalidad → el frontend oculta
 * la ruta/tab correspondiente. Ausencia de una key (o de `features` entero)
 * = habilitada (backward-compat con deploys backend viejos).
 */
export interface TenantFeatures {
  shopify?: boolean;
  conversions?: boolean;
  webhooks?: boolean;
  multi_currency?: boolean;
  bot_api?: boolean;
}

/**
 * Pura — decide si una feature está habilitada para el tenant. Sigue el
 * patrón de `selectPricingMode` (testeable sin QueryClientProvider).
 *
 * Backward-compat absoluto:
 *   - `features` undefined (backend viejo no manda el campo) → true.
 *   - key ausente del objeto → true.
 *   - key explícitamente `false` → false.
 * Solo un `false` explícito oculta algo; cualquier otra cosa habilita.
 */
export function featureEnabled(
  features: TenantFeatures | undefined,
  key: keyof TenantFeatures,
): boolean {
  if (!features) return true;
  return features[key] !== false;
}

/**
 * F4-E: query del config resuelto del tenant. Lectura libre para cualquier
 * rol autenticado (member incluido). El endpoint backend GET /auth/tenant-settings
 * delega a TenantConfigService.get que aplica defaults seguros si el JSONB
 * está parcial.
 *
 * D2: el response incluye `features` (opcional) — funcionalidades habilitadas
 * por tenant. Opcional para backward-compat: un backend viejo que no manda el
 * campo hace que el Layout trate todas las features como habilitadas.
 */
export function useTenantSettings() {
  return useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () =>
      api.get<{
        ok: true;
        config: ResolvedTenantConfig;
        features?: TenantFeatures;
      }>('/auth/tenant-settings'),
    // 401 no reintentes — el Layout ya redirige.
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

/**
 * F4-E: muta el config del tenant. Solo owner/admin (role gate inline en
 * backend). Onsuccess invalida `['tenant-settings']` y `['me']` (cambios
 * en locale/timezone afectan al header del Layout que lee /me). Si la
 * currency cambió también invalidamos `['products']` y `['payments']` porque
 * los precios y montos los formatea el frontend con el currency del tenant.
 */
/** Campos de la cuenta Whatsfy del tenant (id + nombre natural), para el
 *  selector de "Configuración avanzada" (sin IDs crudos). Vienen TODOS: antes
 *  se ocultaban los que no eran texto largo y un campo existente salía como
 *  "no encontrado", lo que invitaba a crear duplicados a mano. `textoLargo`
 *  dice cuáles sirven de destino sin truncar. */
export function useAccountBotFields(enabled: boolean) {
  return useQuery({
    queryKey: ['account-bot-fields'],
    queryFn: () =>
      api.get<{
        ok: true;
        connected: boolean;
        fields: Array<{ id: number; name: string; textoLargo?: boolean }>;
      }>('/auth/tenant-settings/bot-fields'),
    enabled,
    staleTime: 300_000,
  });
}

/**
 * Flows de la cuenta Whatsfy (id + nombre), para elegir el que manda la
 * plantilla aprobada en vez de copiar un id de 13 dígitos. ChatRace no deja
 * crear flows por API, así que el comerciante lo arma en su panel.
 */
/**
 * Sube el logo del comercio y devuelve su URL. Antes los campos aceptaban solo
 * una URL y la interfaz prometía que «el upload llegará en un próximo
 * release»: quien tenía su logo en el escritorio se quedaba sin poder ponerlo.
 */
export function useUploadLogo() {
  return useMutation({
    mutationFn: (file: File) =>
      api.upload<{ ok: true; url: string }>('/auth/tenant-settings/logo', file, 'file'),
  });
}

export function useAccountFlows(enabled: boolean) {
  return useQuery({
    queryKey: ['account-flows'],
    queryFn: () =>
      api.get<{
        ok: true;
        connected: boolean;
        flows: Array<{ id: string; name: string }>;
      }>('/auth/tenant-settings/flows'),
    enabled,
    staleTime: 300_000,
  });
}

/** POST /auth/tenant-settings/bot-fields/provision — "Crear campos del bot":
 *  crea en la cuenta Whatsfy conectada todos los campos inv_* del catálogo
 *  canónico que falten (idempotente; los existentes no se tocan). Si crea
 *  inv_api_token, genera una API key del bot y la deja escrita. */
export function useProvisionBotFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{
        ok: boolean;
        created: number;
        skipped: number;
        failed: Array<{ name: string; error: string }>;
        values: string[];
        apiKeyGenerated: boolean;
        // Nombre → id de los 95, para ver de una qué quedó y con qué id.
        fields?: Array<{
          name: string;
          id: number | null;
          estado: 'creado' | 'ya existía' | 'falló';
          tipoDistinto?: { enLaCuenta: number; esperado: number };
        }>;
        unreadable?: number;
      }>('/auth/tenant-settings/bot-fields/provision', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['account-bot-fields'] });
    },
  });
}

/** Estado humano del sync del bot: última actualización + qué sabe el bot. */
export function useProductsBotSyncStatus() {
  return useQuery({
    queryKey: ['products-bot-sync', 'status'],
    queryFn: () =>
      api.get<{
        ok: true;
        lastSyncAt: string | null;
        products: number;
        paymentMethods: number;
      }>('/auth/tenant-settings/products-bot-sync/status'),
    refetchInterval: 30_000,
  });
}

/** Multi-tenant: detecta los bot fields por NOMBRE en la cuenta del tenant
 *  (el template los crea con el mismo nombre; solo cambia el ID). */
export function useDiscoverBotFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{
        ok: true;
        found: { productos: number | null; formasDePago: number | null; multimedia: number | null; tiendaUrl?: number | null; infoNegocio?: number | null };
      }>('/auth/tenant-settings/bot-fields/discover'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
  });
}

/** "Re-sincronizar ahora": re-vuelca el catálogo al bot field (debounced ~5s). */
export function useRunProductsBotSync() {
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: true; queued: boolean }>(
        '/auth/tenant-settings/products-bot-sync/run',
      ),
  });
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateTenantSettingsInput) =>
      api.patch<{
        ok: true;
        tenant: { id: string; name: string | null };
        config: ResolvedTenantConfig;
      }>('/auth/tenant-settings', dto),
    onSuccess: (_data, variables) => {
      // Cambios que afectan formato/precio en otras vistas — invalidate para
      // que se vuelvan a renderear con el nuevo formato.
      if (
        variables.defaultCurrency !== undefined ||
        variables.numberFormat !== undefined
      ) {
        qc.invalidateQueries({ queryKey: ['products'] });
        qc.invalidateQueries({ queryKey: ['pending-payments'] });
        qc.invalidateQueries({ queryKey: ['dashboard'] });
      }
      // Se espera el refetch: si la mutación resolvía antes, Configuración
      // reconstruía el form con el config viejo y parpadeaba al guardar.
      return Promise.all([
        qc.invalidateQueries({ queryKey: ['tenant-settings'] }),
        qc.invalidateQueries({ queryKey: ['me'] }),
      ]);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    // /auth/logout responde 204 No Content; api.post devuelve null casteado a void.
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      qc.clear();
      // El componente caller redirige a /login.
    },
  });
}

// ===== Warehouses =====

export function useWarehouses(active?: boolean) {
  const q = active === undefined ? '' : `?active=${active}`;
  return useQuery({
    queryKey: ['warehouses', { active }],
    queryFn: () => api.get<Page<Warehouse>>(`/api/warehouses${q}`),
    // 5xx/network = reintenta 1 vez (flap transitorio durante deploy). 401 no
    // reintenta — el Layout ya discrimina y redirige. Sin retry, un blip de
    // red dejaba la lista en empty state ("no hay X todavía") engañoso.
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

export function useCreateWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      code: string;
      name: string;
      city?: string;
      kind?: 'bodega' | 'tienda' | 'oficina';
    }) => api.post<Warehouse>('/api/warehouses', dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warehouses'] }),
  });
}

// ===== Categories =====

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Page<Category>>('/api/categories'),
    // 5xx/network = reintenta 1 vez (flap transitorio durante deploy). 401 no
    // reintenta — el Layout ya discrimina y redirige.
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { name: string; slug?: string }) =>
      api.post<Category>('/api/categories', dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });
}

// ===== Suppliers =====

export function useSuppliers() {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<Page<Supplier>>('/api/suppliers'),
    // 5xx/network = reintenta 1 vez (flap transitorio durante deploy). 401 no
    // reintenta — el Layout ya discrimina y redirige.
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      name: string;
      city?: string;
      leadDays?: number;
      rating?: number;
    }) => api.post<Supplier>('/api/suppliers', dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

// ===== Products =====

export interface ProductsFilter {
  q?: string;
  categoryId?: string;
  supplierId?: string;
  active?: boolean;
  /**
   * Sin este parámetro el backend fuerza `active=true`, así que el listado
   * pierde los archivados. La carga masiva de fotos necesita ver el catálogo
   * completo para poder emparejar por SKU.
   */
  status?: 'active' | 'inactive' | 'all' | 'trash';
  withoutImage?: boolean;
  limit?: number;
  offset?: number;
}

export function useProducts(filter: ProductsFilter = {}) {
  const params = new URLSearchParams();
  if (filter.q) params.set('q', filter.q);
  if (filter.categoryId) params.set('categoryId', filter.categoryId);
  if (filter.supplierId) params.set('supplierId', filter.supplierId);
  if (filter.active !== undefined) params.set('active', String(filter.active));
  if (filter.status) params.set('status', filter.status);
  if (filter.withoutImage) params.set('withoutImage', 'true');
  if (filter.limit) params.set('limit', String(filter.limit));
  if (filter.offset) params.set('offset', String(filter.offset));
  const qs = params.toString() ? `?${params}` : '';
  return useQuery({
    queryKey: ['products', filter],
    queryFn: () => api.get<Page<Product>>(`/api/products${qs}`),
    // 5xx/network = reintenta 1 vez (flap transitorio durante deploy). 401 no
    // reintenta — el Layout ya discrimina y redirige.
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

export function useProduct(id: string | null) {
  return useQuery({
    queryKey: ['products', id],
    queryFn: () => api.get<Product>(`/api/products/${id}`),
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      sku: string;
      name: string;
      description?: string;
      mode?: ProductMode;
      categoryId?: string;
      supplierId?: string;
      cost?: number;
      price?: number;
    }) => api.post<Product>('/api/products', dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: Partial<{
        name: string;
        description: string | null;
        categoryId: string | null;
        supplierId: string | null;
        mode: ProductMode;
        cost: number;
        price: number;
        active: boolean;
      }>;
    }) => api.patch<Product>(`/api/products/${id}`, dto),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['products', vars.id] });
    },
  });
}

/**
 * Marca o desmarca un producto para la vitrina de la tienda. Al marcar va al
 * final de la fila de destacados; al desmarcar vuelve al orden por fecha.
 */
export function useSetDestacado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, destacado }: { id: string; destacado: boolean }) =>
      api.patch<{ id: string; featuredRank: number | null }>(
        `/api/products/${id}/destacado`,
        { destacado },
      ),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['products', v.id] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/products/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      // Archivar cambia el conteo de inactivos, y con él si aparece el botón
      // de limpiar. Sin esto el contador quedaba congelado hasta un minuto
      // (staleTime) y el comerciante concluía que archivar no había hecho nada.
      qc.invalidateQueries({ queryKey: ['catalog-health'] });
    },
  });
}

// ===== Product images =====

export function useUploadProductImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      file,
      variantId,
    }: {
      productId: string;
      file: File;
      variantId?: string | null;
    }) =>
      api.upload<{ id: string; url: string; position: number }>(
        `/api/products/${productId}/images`,
        file,
        'file',
        variantId !== undefined && variantId !== null ? { variantId } : undefined,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products', vars.productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['product-images', vars.productId] });
    },
  });
}

// ─── Preguntas frecuentes por producto ─────────────────────────────────────
// El backend está completo desde B3 y el volcado al bot las incluye; lo que
// faltaba era dónde escribirlas. La casilla «Preguntas frecuentes» del volcado
// viene ENCENDIDA por defecto, así que hasta ahora el panel prometía algo que
// no tenía formulario.

export interface ProductFaq {
  id: string;
  question: string;
  answer: string;
  position: number;
  active: boolean;
}

export function useProductFaqs(productId: string | null) {
  return useQuery({
    queryKey: ['product-faqs', productId],
    queryFn: () => api.get<ProductFaq[]>(`/api/products/${productId}/faqs`),
    enabled: !!productId,
  });
}

/** Invalida el producto (el bot lo re-vuelca) y la lista de preguntas. */
function invalidarFaqs(qc: ReturnType<typeof useQueryClient>, productId: string) {
  qc.invalidateQueries({ queryKey: ['product-faqs', productId] });
  qc.invalidateQueries({ queryKey: ['products', productId] });
}

export function useCreateProductFaq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      question,
      answer,
    }: {
      productId: string;
      question: string;
      answer: string;
    }) => api.post<ProductFaq>(`/api/products/${productId}/faqs`, { question, answer }),
    onSuccess: (_d, v) => invalidarFaqs(qc, v.productId),
  });
}

export function useUpdateProductFaq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      faqId,
      ...body
    }: {
      productId: string;
      faqId: string;
      question?: string;
      answer?: string;
      position?: number;
      active?: boolean;
    }) => api.patch<ProductFaq>(`/api/products/${productId}/faqs/${faqId}`, body),
    onSuccess: (_d, v) => invalidarFaqs(qc, v.productId),
  });
}

export function useDeleteProductFaq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, faqId }: { productId: string; faqId: string }) =>
      api.del<void>(`/api/products/${productId}/faqs/${faqId}`),
    onSuccess: (_d, v) => invalidarFaqs(qc, v.productId),
  });
}

export function useDeleteProductImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, imageId }: { productId: string; imageId: string }) =>
      api.del<void>(`/api/products/${productId}/images/${imageId}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products', vars.productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useReorderProductImages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, order }: { productId: string; order: string[] }) =>
      api.patch(`/api/products/${productId}/images/reorder`, { order }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products', vars.productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// ===== Variants management =====

export function useCreateVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      dto,
    }: {
      productId: string;
      // Permite null en cost/price/dimensions: shape simétrica con update.
      // El backend ignora null en create (no persiste el campo) — el form
      // del frontend puede pasar null indistintamente.
      dto: {
        sku: string;
        label?: string;
        barcode?: string;
        weight?: number;
        imageUrl?: string;
        swatchImageUrl?: string | null;
        cost?: number | null;
        price?: number | null;
        dimensions?: { w?: number; h?: number; d?: number } | null;
        attributes?: Record<string, string>;
      };
    }) => api.post(`/api/products/${productId}/variants`, dto),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products', vars.productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      variantId,
      dto,
    }: {
      productId: string;
      variantId: string;
      // null permitido en cost/price/dimensions: limpia el override → hereda del Product.
      // null en swatchImageUrl: quita el swatch (vuelve al fallback color de attributes).
      dto: Partial<{
        sku: string;
        label: string;
        barcode: string;
        weight: number;
        imageUrl: string;
        swatchImageUrl: string | null;
        active: boolean;
        cost: number | null;
        price: number | null;
        dimensions: { w?: number; h?: number; d?: number } | null;
      }>;
    }) => api.patch(`/api/products/${productId}/variants/${variantId}`, dto),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products', vars.productId] });
      // También invalidar el listado raíz: la lista de productos muestra
      // el primer variante (precio, label) y stale-state se nota en UX.
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeleteVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, variantId }: { productId: string; variantId: string }) =>
      api.del<void>(`/api/products/${productId}/variants/${variantId}`),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products', vars.productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useBulkGenerateVariants() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      attributes,
      skuPattern,
    }: {
      productId: string;
      attributes: Record<string, string[]>;
      skuPattern?: string;
    }) =>
      api.post<{
        ok: true;
        created: number;
        variants: Array<{
          id: string;
          sku: string;
          label: string | null;
          attributes: Record<string, string>;
        }>;
      }>(`/api/products/${productId}/variants/bulk-generate`, { attributes, skuPattern }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products', vars.productId] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateVariantAttributes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      variantId,
      attributes,
    }: {
      productId: string;
      variantId: string;
      attributes: Record<string, string>;
    }) =>
      api.patch<{ ok: true }>(
        `/api/products/${productId}/variants/${variantId}/attributes`,
        { attributes },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products', vars.productId] });
    },
  });
}

export function useReassignImageVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      imageId,
      variantId,
    }: {
      productId: string;
      imageId: string;
      variantId: string | null;
    }) =>
      api.patch<{ ok: true }>(
        `/api/products/${productId}/images/${imageId}/variant`,
        { variantId },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['products', vars.productId] });
      qc.invalidateQueries({ queryKey: ['product-images', vars.productId] });
    },
  });
}

// ===== Inventory level (reorder_point) =====

export function useUpdateInventoryLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      levelId,
      reorderPoint,
    }: {
      levelId: string;
      reorderPoint: number | null;
    }) => api.patch(`/api/inventory/levels/${levelId}`, { reorderPoint }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Bulk update atómico de reorder points: usado por el flow de onboarding
 * (`/reorder-points`) para configurar todos los umbrales en un solo submit.
 * Si una fila falla en el servidor, ninguna persiste — invalidamos las
 * queries solo en onSuccess.
 */
export function useBulkUpdateReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: Array<{ levelId: string; reorderPoint: number | null }>) =>
      api.post<{ ok: true; count: number }>('/api/inventory/levels/bulk-update-reorder', {
        items,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// ===== Suppliers extra =====

export function useUpdateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: Partial<{
        name: string;
        city: string;
        leadDays: number;
        rating: number;
        notes: string;
        terms: string;
      }>;
    }) => api.patch<Supplier>(`/api/suppliers/${id}`, dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/suppliers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers'] }),
  });
}

// ===== Inventory =====

export function useInventoryLevels(
  filter: { variantId?: string; warehouseId?: string; limit?: number; offset?: number } = {},
) {
  const params = new URLSearchParams();
  if (filter.variantId) params.set('variantId', filter.variantId);
  if (filter.warehouseId) params.set('warehouseId', filter.warehouseId);
  if (filter.limit) params.set('limit', String(filter.limit));
  if (filter.offset) params.set('offset', String(filter.offset));
  const qs = params.toString() ? `?${params}` : '';
  return useQuery({
    queryKey: ['inventory-levels', filter],
    queryFn: () => api.get<Page<InventoryLevel>>(`/api/inventory/levels${qs}`),
    // 5xx/network = reintenta 1 vez (flap transitorio durante deploy). 401 no
    // reintenta — el Layout ya discrimina y redirige.
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

/**
 * Hook canónico para el badge "stock bajo" del sidebar/topbar.
 *
 * Reemplaza el cálculo cliente-side que vivía en Layout.tsx (groupBy variantId
 * sobre `useInventoryLevels()` y conteo de variants con SUM < 5). Ese cálculo
 * estaba truncado por el `limit=100` default de `/api/inventory/levels`: para
 * tenants con >100 levels (Kabuky ~300+ variants × warehouses) el contador
 * solo veía la primera página y subreportaba el problema.
 *
 * El endpoint backend hace COUNT directo en SQL con la misma semántica que el
 * dashboard (variants tracked con total_qty <= 0 OR total_qty < max(reorder)).
 */
export function useLowStockCount() {
  return useQuery({
    queryKey: ['inventory-low-stock-summary'],
    queryFn: () => api.get<{ count: number }>('/api/inventory/low-stock-summary'),
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

export function useKardex(productId: string | null) {
  return useQuery({
    queryKey: ['kardex', productId],
    queryFn: () => api.get<Page<Movement>>(`/api/products/${productId}/movements`),
    enabled: !!productId,
  });
}

export interface MovementsFilter {
  kind?: 'entrada' | 'salida' | 'ajuste' | 'merma' | 'transferencia';
  warehouseId?: string;
  variantId?: string;
  startDate?: string;
  endDate?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

// ===== Channels =====

/**
 * V3.1 — deeplinks omnichannel persistidos en `Channel.deeplinkConfig` (JSONB).
 * El backend valida con regex estricto (E.164 sin '+', usernames sin '@', URL
 * https). El frontend debe espejar exactamente esas reglas — si el regex del
 * cliente se desvía del server, el form pasa y el PATCH 400-ea opaco.
 */
export interface ChannelDeeplinkConfig {
  whatsappPhone?: string | null;
  messengerPageId?: string | null;
  instagramUsername?: string | null;
  telegramBotName?: string | null;
  webchatUrl?: string | null;
  viberPhone?: string | null;
  smsPhone?: string | null;
}

export interface Channel {
  id: string;
  kind:
    | 'shopify'
    | 'woocommerce'
    | 'tiendanube'
    | 'mercadolibre'
    | 'google_sheets'
    | 'excel'
    | 'custom_api'
    | 'manual'
    | 'whatsfy_ecom';
  name: string;
  status: 'active' | 'paused' | 'error' | 'disconnected';
  config: Record<string, unknown>;
  lastSyncAt: string | null;
  errorMsg: string | null;
  /** Lista de precios asignada al canal (F3 multi-currency). `null` =
   *  canal usa la lista predeterminada del tenant. */
  priceListId?: string | null;
  /** V3.1 — config omnichannel del canal. Member NO la ve (backend la oculta). */
  deeplinkConfig?: ChannelDeeplinkConfig | null;
  _count?: { productMaps: number };
}

export interface SyncEvent {
  id: string;
  kind: 'sync_start' | 'sync_ok' | 'conflict' | 'error' | 'import' | 'paused';
  msg: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export function useChannels() {
  return useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<{ items: Channel[] }>('/api/channels'),
  });
}

export function useConnectWhatsfyEcom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { name?: string }) =>
      api.post<Channel>('/api/channels/whatsfy-ecom', dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
}

export function usePushProductToChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      productId,
      externalId,
    }: {
      channelId: string;
      productId: string;
      externalId?: string;
    }) =>
      api.post<{
        ok: boolean;
        product: { id: string; sku: string; name: string };
        externalId: string;
        stockPushed: number;
      }>(`/api/channels/${channelId}/push-product`, { productId, externalId }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['channels'] });
      qc.invalidateQueries({ queryKey: ['sync-events'] });
      qc.invalidateQueries({ queryKey: ['product-maps', vars.channelId] });
    },
  });
}

export function useChannelSyncEvents(channelId: string | null) {
  return useQuery({
    queryKey: ['sync-events', channelId],
    queryFn: () => api.get<{ items: SyncEvent[] }>(`/api/channels/${channelId}/sync-events`),
    enabled: !!channelId,
  });
}

export interface ChannelProductMap {
  productId: string;
  variantId: string;
  variantSku: string;
  /**
   * NULL = mapeo creado en estado "pendiente sync" — típicamente por el
   * flow `POST /api/products/:id/duplicate` con `withChannelMappings:true`
   * (CM-3). El comerciante debe sincronizar manualmente desde el canal
   * para que el adapter le asigne el external ID real.
   *
   * Antes de CM-1 esta columna era NOT NULL — los maps existentes pre-CM
   * siguen viniendo con string non-null.
   */
  externalId: string | null;
  externalSku: string | null;
  lastSyncedAt: string | null;
}

export function useChannelProductMaps(channelId: string | null) {
  return useQuery({
    queryKey: ['product-maps', channelId],
    queryFn: () =>
      api.get<{ items: ChannelProductMap[] }>(`/api/channels/${channelId}/product-maps`),
    enabled: !!channelId,
  });
}

export interface ChannelLocation {
  id: string;        // gid://shopify/Location/<num>
  name: string;
  address: string | null;
}

export function useChannelLocations(channelId: string | null) {
  return useQuery({
    queryKey: ['channel-locations', channelId],
    queryFn: () =>
      api.get<{ items: ChannelLocation[] }>(`/api/channels/${channelId}/locations`),
    enabled: !!channelId,
  });
}

export function useSetWarehouseExternalId() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { warehouseId: string; kind: string; externalId: string | null }) =>
      api.patch<Warehouse>(`/api/warehouses/${vars.warehouseId}/external-ids`, {
        kind: vars.kind,
        externalId: vars.externalId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouses'] });
    },
  });
}

export interface FullSyncResult {
  totalSnapshots: number;
  matchedAndMapped: number;
  unmappedProducts: number;
  unmappedWarehouses: number;
  errors: number;
  durationMs: number;
}

/**
 * Re-registra los webhook subscriptions Shopify para los topics que
 * quedaron como null. El callback OAuth los registra best-effort; si
 * algunos fallan (transient, scope timing), la UI surface el botón.
 */
export function useReregisterShopifyWebhooks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) =>
      api.post<{
        ok: true;
        total: number;
        succeeded: number;
        failed: number;
        webhookIds: Record<string, string | null>;
      }>(`/api/channels/${channelId}/reregister-webhooks`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

/**
 * Start Shopify install flow: pide al backend un install token short-lived
 * (vinculado a la sesión actual via cookie) y devuelve la URL completa
 * para abrir en popup. La popup NO necesita cookies — auth via token en
 * query param. Resuelve el problema de Safari ITP / Chrome SameSite que
 * bloquean cookies cross-site al popup desde iframe.
 */
export function useStartShopifyInstall() {
  return useMutation({
    mutationFn: (shop: string) =>
      api.post<{ installUrl: string; expiresInSeconds: number }>(
        '/auth/shopify/install/start',
        { shop },
      ),
  });
}

export function useRunChannelFullSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) =>
      api.post<FullSyncResult>(`/api/channels/${channelId}/full-sync`),
    onSuccess: (_data, channelId) => {
      // H-D1 (audit 2026-05-07): el sync escribe inventory_levels.qty
      // (en delta mode también crea Movement). Sin invalidar
      // ['inventory-levels'] y ['dashboard'], el panel y los KPIs quedan
      // stale hasta refetch on focus. Patrón consistente con
      // useAdjustInventory / useTransferStock.
      qc.invalidateQueries({ queryKey: ['channels'] });
      qc.invalidateQueries({ queryKey: ['sync-events', channelId] });
      qc.invalidateQueries({ queryKey: ['product-maps', channelId] });
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// ===== Draft orders (Shopify) =====

/**
 * Sprint 2 (2026-05-08): el módulo dispara `draftOrderCreate` contra Shopify
 * para que el bot del comerciante mande un link de checkout pre-llenado al
 * cliente final. El endpoint privado (`/api/channels/:id/draft-orders`,
 * cookie-auth) es lo que la UI admin usa para probar el flow desde
 * `ShopifyDetailModal`. Existe un análogo público
 * (`/api/public/v1/draft-orders/:channelId`, Bearer + scope `orders:write`)
 * pensado para que el bot lo llame.
 */
export interface DraftOrderLineInput {
  sku: string;
  qty: number;
}

export interface DraftOrderCustomerInput {
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}

export interface CreateDraftOrderInput {
  lineItems: DraftOrderLineInput[];
  customer: DraftOrderCustomerInput;
  type?: 'invoice' | 'checkout';
  note?: string;
}

export interface CreateDraftOrderResponse {
  ok: boolean;
  draftOrderId: string;
  invoiceUrl: string | null;
  checkoutUrl: string | null;
  totalAmount: number;
  currency: string;
}

export function useCreateDraftOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      body,
    }: {
      channelId: string;
      body: CreateDraftOrderInput;
    }) =>
      api.post<CreateDraftOrderResponse>(
        `/api/channels/${channelId}/draft-orders`,
        body,
      ),
    onSuccess: (_data, vars) => {
      // El draft-order genera un SyncEvent en Shopify ↔ módulo; refresh del
      // log dentro del modal para que el merchant vea el evento sin recargar.
      qc.invalidateQueries({ queryKey: ['sync-events', vars.channelId] });
      qc.invalidateQueries({ queryKey: ['channels'] });
      // Sprint 3: la lista de draft orders dentro del mismo modal se
      // refresca al instante para que el merchant vea el pedido recién
      // creado sin tener que recargar.
      qc.invalidateQueries({ queryKey: ['channels', vars.channelId, 'draft-orders'] });
    },
  });
}

/**
 * Sprint 3 (2026-05-08): listado de draft orders creados desde el módulo o
 * desde el bot. La fuente es la tabla `DraftOrderRequest` (no Shopify directo)
 * porque el módulo ya rastrea el cart→variant mapping y el `paid` que dispara
 * el `Movement(salida)` automático.
 *
 * El `status` viene de la tabla local: `open` (creado), `completed` (pagado en
 * Shopify, generó Movement), `cancelled` (cliente abandonó o expiró el lock).
 */
export interface DraftOrderItem {
  sku: string;
  qty: number;
  unitPrice: number;
  variantId?: string;
}

export interface DraftOrderRequest {
  id: string;
  shopifyDraftOrderId: string;
  items: DraftOrderItem[];
  customerEmail: string;
  customerPhone?: string;
  customerFirstName?: string;
  customerLastName?: string;
  note?: string;
  totalAmount: string | number;
  currencyCode: string;
  invoiceUrl: string;
  status: 'open' | 'completed' | 'cancelled';
  createdAt: string;
  paidAt?: string;
  cancelledAt?: string;
}

export interface DraftOrdersFilters {
  status?: 'open' | 'completed' | 'cancelled';
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export function useDraftOrders(
  channelId: string | null,
  filters: DraftOrdersFilters = {},
) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString() ? `?${params}` : '';
  return useQuery({
    queryKey: ['channels', channelId, 'draft-orders', filters],
    queryFn: () =>
      api.get<Page<DraftOrderRequest>>(
        `/api/channels/${channelId}/draft-orders${qs}`,
      ),
    enabled: !!channelId,
  });
}

// ===== Imports =====

export interface ImportJob {
  id: string;
  kind: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  sourceFile: string | null;
  totalRows: number | null;
  processedRows: number;
  errors: Array<{ row: number; reason: string; raw: Record<string, unknown> }> | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface ImportMapping {
  sku: string;
  name: string;
  parentSku?: string;
  label?: string;
  description?: string;
  qty?: string;
  warehouseCode?: string;
  category?: string;
  cost?: string;
  price?: string;
  imageUrl?: string;
  variantCost?: string;
  variantPrice?: string;
  dimW?: string;
  dimH?: string;
  dimD?: string;
  weight?: string;
  barcode?: string;
}

export function useCreateImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { filename: string; mapping: ImportMapping; rows: Record<string, unknown>[] }) =>
      api.post<{ id: string; status: string; totalRows: number; filename: string }>(
        '/api/imports',
        dto,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports'] });
    },
  });
}

// ============================================================================
// CSV legacy preview/apply (P0-#4)
// ============================================================================

export interface CsvPreviewError {
  row: number;
  reason: string;
  raw: Record<string, unknown>;
}

export interface CsvPreviewResponse {
  previewToken: string;
  expiresAt: string;
  summary: {
    totalRows: number;
    groupsCount: number;
    validRows: number;
    errorRows: number;
    creates: number;
    updates: number;
    warnings: string[];
  };
  errors: CsvPreviewError[];
  plan: {
    newProductSkus: string[];
    existingProductSkus: string[];
    newVariants: number;
    stockMovements: number;
    groupsCount: number;
    sampleDiffs: Array<unknown>;
  };
}

export function usePreviewCsvImport() {
  return useMutation({
    mutationFn: (dto: { filename: string; mapping: ImportMapping; rows: Record<string, unknown>[] }) =>
      api.post<CsvPreviewResponse>('/api/imports/preview', dto),
  });
}

export function useApplyCsvImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { previewToken: string }) =>
      api.post<{ id: string; status: string; totalRows: number; filename: string }>(
        '/api/imports/apply',
        dto,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports'] });
    },
  });
}

export function useImports() {
  return useQuery({
    queryKey: ['imports'],
    queryFn: () => api.get<{ items: ImportJob[] }>('/api/imports'),
  });
}

export function useImport(id: string | null) {
  return useQuery({
    queryKey: ['imports', id],
    queryFn: () => api.get<ImportJob>(`/api/imports/${id}`),
    enabled: !!id,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === 'queued' || status === 'running' ? 1500 : false;
    },
  });
}

export function useMovements(filter: MovementsFilter = {}) {
  const params = new URLSearchParams();
  if (filter.kind) params.set('kind', filter.kind);
  if (filter.warehouseId) params.set('warehouseId', filter.warehouseId);
  if (filter.variantId) params.set('variantId', filter.variantId);
  if (filter.startDate) params.set('startDate', filter.startDate);
  if (filter.endDate) params.set('endDate', filter.endDate);
  if (filter.q && filter.q.trim()) params.set('q', filter.q.trim());
  if (filter.limit) params.set('limit', String(filter.limit));
  if (filter.offset) params.set('offset', String(filter.offset));
  const qs = params.toString() ? `?${params}` : '';
  return useQuery({
    queryKey: ['movements', filter],
    queryFn: () =>
      api.get<
        Page<
          Movement & {
            variant?: {
              id: string;
              sku: string;
              label: string | null;
              product?: { id: string; name: string; sku: string };
            };
            user?: { email: string | null } | null;
          }
        >
      >(`/api/movements${qs}`),
    // 5xx/network = reintenta 1 vez (flap transitorio durante deploy). 401 no
    // reintenta — el Layout ya discrimina y redirige.
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

export function useAdjustInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      variantId: string;
      warehouseId: string;
      kind: 'entrada' | 'salida' | 'ajuste' | 'merma';
      qty: number;
      reason?: string;
      externalId?: string;
    }) => api.post<{ movement: Movement; deduplicated: boolean; levelAfter: number }>('/api/inventory/adjust', dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['kardex'] });
      // Invalidar TODO el árbol ['products', ...] (lista + detalle de cada
      // producto). Antes invalidaba ['products', vars.variantId] pero el
      // detalle del drawer usa ['products', PRODUCTID] (no variantId), así
      // que el invalidate era no-op contra el drawer y mostraba stock
      // optimistic forever. Prefix-match de TanStack v5 con key bare cubre
      // lista + cualquier ['products', X].
      qc.invalidateQueries({ queryKey: ['products'] });
      // Dashboard ahora consume server-side stats (H-IV2). Sin invalidar
      // explícito, KPIs (totalQty, inventoryValue, lowStockSkus, topLowStock)
      // quedan stale tras adjust/transfer hasta unmount + remount.
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ===== Categories admin =====

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dto }: { id: string; name?: string; slug?: string }) =>
      api.patch<Category>(`/api/categories/${id}`, dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });
}

// ===== Transfers =====

export function useTransferStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      variantId: string;
      fromWarehouseId: string;
      toWarehouseId: string;
      qty: number;
      reason?: string;
    }) =>
      api.post<{
        ok: true;
        transferId: string;
        movementIds: string[];
        fromBalance: number;
        toBalance: number;
      }>(`/api/inventory/transfer`, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['movements'] });
      qc.invalidateQueries({ queryKey: ['kardex'] });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ===== Products bulk actions (Batch 1 backend) =====

export function useBulkArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { productIds: string[]; active: boolean }) =>
      api.post<{ ok: true; updated: number; message: string }>(
        '/api/products/bulk-archive',
        dto,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useBulkUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { productIds: string[]; categoryId: string | null }) =>
      api.post<{ ok: true; updated: number; message: string }>(
        '/api/products/bulk-update-category',
        dto,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useBulkUpdatePrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      productIds: string[];
      setPrice?: number;
      pricePercentDelta?: number;
      priceAbsoluteDelta?: number;
      applyToCost?: boolean;
    }) =>
      api.post<{
        ok: true;
        updated: number;
        skipped?: number;
        message: string;
      }>('/api/products/bulk-update-price', dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useBulkUpdateVariantPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      variantIds: string[];
      setPrice?: number;
      pricePercentDelta?: number;
      priceAbsoluteDelta?: number;
      applyToCost?: boolean;
    }) =>
      api.post<{
        ok: true;
        updated: number;
        skipped?: number;
        message: string;
      }>('/api/products/bulk-update-variant-price', dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useBulkUpdateProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: {
      productIds: string[];
      cost?: number;
      price?: number;
      categoryId?: string | null;
      supplierId?: string | null;
      active?: boolean;
    }) =>
      api.post<{ ok: true; updated: number; message: string }>(
        '/api/products/bulk-update',
        dto,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

/**
 * P2 — snapshot agregado de la salud del catálogo. Owner/admin only.
 * Pensado para un banner en /productos. staleTime alto (60s) — no necesita
 * refresh frecuente; se invalida explícitamente tras un bulk-archive o
 * bulk-hard-delete que altere los counts.
 */
export interface CatalogHealth {
  totalActive: number;
  totalInactive: number;
  total: number;
  withoutCopPrice: number;
  withoutUsdPrice: number;
  withoutStock: number;
  withoutBarcode: number;
  withoutImage: number;
  archivableInactives: number;
  /** Productos en la papelera esperando eliminarse. */
  inTrash: number;
  /** Días que un producto espera en la papelera antes de eliminarse. */
  trashRetentionDays: number;
  hasUsdPriceList: boolean;
  hasDefaultPriceList: boolean;
  defaultCurrency: string | null;
}

export function useCatalogHealth() {
  return useQuery({
    queryKey: ['catalog-health'],
    queryFn: () => api.get<CatalogHealth>('/api/products/catalog-health'),
    staleTime: 60_000,
    // 403 (member) tiene shape 'status' en el error normalizado; no reintentar.
    retry: (failureCount, error) =>
      failureCount < 1 &&
      !(error instanceof Error && 'status' in error && (error as { status: number }).status === 403),
  });
}

export function useBulkHardDeleteInactives() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { confirm: true }) =>
      api.post<{
        ok: true;
        deleted: number;
        skipped: number;
        skippedByReason: {
          movements: number;
          purchase_order_lines: number;
          reservations: number;
          channel_product_maps: number;
        };
        errors: Array<{ productId: string; sku: string; error: string }>;
        partial: boolean;
        message: string;
      }>('/api/products/bulk-hard-delete-inactives', dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['catalog-health'] });
    },
  });
}

// ===== Papelera de productos =====

/** Por qué un producto no se pudo enviar/restaurar/eliminar. */
export type TrashRejectReason =
  | 'not_found'
  | 'active'
  | 'already_in_trash'
  | 'not_in_trash';
export type TrashKeepReason = 'purchase_order_lines' | 'active_reservations';

function invalidateTrash(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['products'] });
  qc.invalidateQueries({ queryKey: ['catalog-health'] });
}

/** Manda archivados a la papelera (3 días para arrepentirse). */
export function useBulkSoftDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { ids: string[] }) =>
      api.post<{
        ok: true;
        moved: number;
        rejected: Array<{ id: string; reason: TrashRejectReason }>;
        purgeAt: string;
        message: string;
      }>('/api/products/bulk-soft-delete', dto),
    onSuccess: () => invalidateTrash(qc),
  });
}

/** Saca de la papelera: el producto vuelve a Inactivos. */
export function useBulkRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { ids: string[] }) =>
      api.post<{
        ok: true;
        restored: number;
        rejected: Array<{ id: string; reason: TrashRejectReason }>;
        message: string;
      }>('/api/products/bulk-restore', dto),
    onSuccess: () => invalidateTrash(qc),
  });
}

/** Elimina ahora lo que está en la papelera (los ids, o toda si no se mandan). */
export function usePurgeTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { ids?: string[] }) =>
      api.post<{
        ok: true;
        purged: number;
        kept: Array<{ id: string; sku: string; reason: TrashKeepReason }>;
        errors: Array<{ id: string; sku: string; error: string }>;
        partial: boolean;
        message: string;
      }>('/api/products/purge-trash', dto),
    onSuccess: () => invalidateTrash(qc),
  });
}

export function useDuplicateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      dto,
    }: {
      productId: string;
      dto: { newSku?: string; withVariants?: boolean; withImages?: boolean };
    }) => api.post(`/api/products/${productId}/duplicate`, dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

// ===== Audit log (Batch 2 backend) =====

export function useAuditByEntity(
  entityType: string | null,
  entityId: string | null,
) {
  return useQuery({
    queryKey: ['audit-by-entity', entityType, entityId],
    queryFn: () =>
      api.get<{ items: AuditEntry[] }>(
        `/api/audit/by-entity/${entityType}/${entityId}`,
      ),
    enabled: !!entityType && !!entityId,
  });
}

/** Filtros del audit trail global. Todos opcionales. */
export interface AuditFilters {
  entityType?: string;
  entityId?: string;
  userId?: string;
  action?: string;
  /** ISO 8601 (e.g. "2026-05-01T00:00:00Z"). */
  from?: string;
  /** ISO 8601 (incluido en el rango). */
  to?: string;
  limit?: number;
  offset?: number;
}

/**
 * Lista paginada de audit logs con filtros opcionales. Endpoint
 * `GET /api/audit` accesible a todos los roles (transparencia interna).
 * Cada item viene enriquecido con `userEmail` desde el backend.
 */
export function useAuditLog(filters: AuditFilters) {
  return useQuery({
    queryKey: ['audit-log', filters],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.entityType) sp.set('entityType', filters.entityType);
      if (filters.entityId) sp.set('entityId', filters.entityId);
      if (filters.userId) sp.set('userId', filters.userId);
      if (filters.action) sp.set('action', filters.action);
      if (filters.from) sp.set('from', filters.from);
      if (filters.to) sp.set('to', filters.to);
      if (filters.limit !== undefined) sp.set('limit', String(filters.limit));
      if (filters.offset !== undefined)
        sp.set('offset', String(filters.offset));
      const qs = sp.toString();
      return api.get<{
        items: AuditEntry[];
        total: number;
        limit: number;
        offset: number;
      }>(`/api/audit${qs ? `?${qs}` : ''}`);
    },
  });
}

// ===== API keys (Sprint 1 bot+checkout) =====

/**
 * Lista de API keys del tenant. Endpoint owner/admin-only (RolesGuard backend).
 * El secret nunca viene en este listado — solo en POST /api/api-keys.
 */
export function useApiKeys() {
  return useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<{ items: ApiKey[] }>('/api/api-keys'),
  });
}

/**
 * Crea una nueva API key. La response trae `secret` PLAINTEXT (one-shot)
 * más un helper `fullToken` ya armado para mostrar al user.
 */
export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      label: string;
      scopes: string[];
      /** V3 — códigos de bodega permitidos. `[]` o ausencia = todas. */
      warehouseScopes?: string[];
    }) => api.post<ApiKeyCreated>('/api/api-keys', vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
}

/**
 * Agrega permisos a una key existente. Solo agrega: el backend no admite
 * quitarlos, para que un descuido no deje sin `orders:write` al bot de alguien.
 * Sirve para habilitar una rama nueva —postventa, por ejemplo— sin obligar a
 * crear otra key y cambiar el token en Whatsfy.
 */
export function useAddApiKeyScopes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; add: string[] }) =>
      api.patch<{ ok: true; scopes: string[] }>(
        `/api/api-keys/${vars.id}/scopes`,
        { add: vars.add },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
}

/**
 * Revoca (delete) una API key. Bots/flows que la usen empiezan a recibir
 * 401 inmediato. Idempotente desde el punto de vista del cliente: si dos
 * tabs revocan la misma key en simultáneo, la segunda recibe 404 del backend
 * y el `mutationFn` lo absorbe — el caller ve éxito y la lista se invalida
 * igual (la realidad es que la key ya no existe, que es lo que el user pidió).
 * `onSettled` garantiza que la invalidación corre tanto en éxito normal como
 * en el path "ya estaba revocada".
 */
export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        await api.del<void>(`/api/api-keys/${id}`);
        return { ok: true as const, alreadyMissing: false as const };
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { ok: true as const, alreadyMissing: true as const };
        }
        throw err;
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
}

// ===== Payments (Sprint 4) =====
//
// Bandeja de pagos por transferencia: el bot crea el PendingPayment vía
// API pública con pantallazo del cliente; el admin verifica/rechaza desde
// el panel. Detalle de arquitectura en
// docs/research/2026-05-08-bank-transfer-architecture.md.

/** Listado de métodos de pago (cuentas bancarias / Bre-B / efectivo) que
 *  el bot le muestra al cliente. Por default solo activos; pasar
 *  `includeInactive=true` para ver soft-deleted (owner/admin only). */
export function usePaymentMethods(includeInactive = false) {
  const qs = includeInactive ? '?includeInactive=true' : '';
  return useQuery({
    queryKey: ['payment-methods', { includeInactive }],
    queryFn: () => api.get<{ items: PaymentMethod[] }>(`/api/payment-methods${qs}`),
  });
}

/** Superset del `PaymentMethodType` legacy (`bank_transfer|bre_b|cash|other`)
 *  ampliado con kinds manuales multi-currency introducidos en V3.4:
 *
 *  - `manual_transfer` (alias de `bank_transfer` semánticamente; el backend
 *    canónico sigue siendo `bank_transfer`, pero el UI ofrece la etiqueta
 *    "Transferencia bancaria" como kind elegible).
 *  - `manual_zelle`  → Zelle USA (USD). Instrucciones piden email/phone.
 *  - `manual_wire`   → wire SWIFT internacional (USD/EUR). Instrucciones piden
 *    banco intermediario + SWIFT + cuenta.
 *
 *  El backend acepta strings adicionales aditivamente (`@@unique` de display
 *  no rompe). `PaymentMethodType` original sigue siendo el tipo canónico del
 *  contrato API — este alias amplía el rango ACEPTADO por el form. */
export type PaymentMethodKind = PaymentMethodType | 'manual_zelle' | 'manual_wire';

export interface CreatePaymentMethodDto {
  type: PaymentMethodKind;
  displayName: string;
  bankName?: string;
  accountNumber?: string;
  accountType?: string;
  accountHolderName?: string;
  accountHolderId?: string;
  breBKey?: string;
  instructions?: string;
  displayOrder?: number;
  /** ISO 4217 (3 chars upper). Null/undefined = el método aparece en cualquier
   *  moneda (legacy behavior). Cuando se setea, el método se filtra para
   *  mostrarse solo en carritos de esa moneda — útil para Zelle/wire USD. */
  currencyCode?: string | null;
  /** Canales a los que aplica el método. Array vacío (o ausente) = visible
   *  en todos los canales del tenant. */
  channelIds?: string[];
}

export function useCreatePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePaymentMethodDto) =>
      api.post<PaymentMethod>('/api/payment-methods', dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-methods'] }),
  });
}

export interface UpdatePaymentMethodDto {
  displayName?: string;
  bankName?: string;
  accountNumber?: string;
  accountType?: string;
  accountHolderName?: string;
  accountHolderId?: string;
  breBKey?: string;
  instructions?: string;
  displayOrder?: number;
  isActive?: boolean;
  /** ISO 4217 (3 chars upper). `null` explícito limpia la restricción; omitir
   *  = no tocar el valor actual. */
  currencyCode?: string | null;
  /** Canales a los que aplica el método. Ausente = no tocar la asignación
   *  existente; `[]` = volver a "todos los canales"; con UUIDs = solo esos. */
  channelIds?: string[];
}

export function useUpdatePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePaymentMethodDto }) =>
      api.patch<PaymentMethod>(`/api/payment-methods/${id}`, dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-methods'] }),
  });
}

export function useDeletePaymentMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/payment-methods/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-methods'] }),
  });
}

export interface PendingPaymentsFilter {
  status?: PendingPaymentStatus;
  /** Buscar en customerPhone, customerName, customerEmail. */
  q?: string;
  /** ISO timestamp inclusivo. */
  from?: string;
  to?: string;
  /** Filtra por canal de origen del pago. UUID. */
  channelId?: string;
  /** Filtra por moneda del pago (ISO-4217, ej. 'COP', 'USD'). */
  currencyCode?: string;
  /**
   * Estado del ENVÍO, distinto del estado del pago: `por_despachar` son los ya
   * cobrados y sin guía; `despachado`, los que ya salieron.
   */
  shipping?: 'por_despachar' | 'despachado';
  /** De dónde vino: tienda web, chat (bot) u otro (panel/API). */
  origen?: 'tienda' | 'chat' | 'otro';
  limit?: number;
  offset?: number;
}

/** Lista paginada. Soporta filtros por status, búsqueda libre, rango de
 *  fechas, canal y moneda. La UI lo usa para el badge del nav
 *  (limit:1, status active) y para la bandeja principal.
 *
 *  Real-time refresh: la bandeja se refetchea cada 30s para mostrar pagos
 *  nuevos sin necesidad de F5 — los pagos pendientes del bot pueden llegar
 *  mientras el operador tiene la pestaña abierta. */
export function usePendingPayments(filter: PendingPaymentsFilter = {}) {
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  if (filter.q) params.set('q', filter.q);
  if (filter.from) params.set('from', filter.from);
  if (filter.to) params.set('to', filter.to);
  if (filter.channelId) params.set('channelId', filter.channelId);
  if (filter.currencyCode) params.set('currencyCode', filter.currencyCode);
  if (filter.shipping) params.set('shipping', filter.shipping);
  if (filter.origen) params.set('origen', filter.origen);
  if (filter.limit) params.set('limit', String(filter.limit));
  if (filter.offset !== undefined) params.set('offset', String(filter.offset));
  const qs = params.toString() ? `?${params}` : '';
  return useQuery({
    queryKey: ['pending-payments', filter],
    queryFn: () => api.get<Page<PendingPayment>>(`/api/payments/pending${qs}`),
    refetchInterval: 30_000,
  });
}

/** Detalle de un pago + verificationLogs (timeline). */
export function usePaymentDetail(id: string | null) {
  return useQuery({
    queryKey: ['pending-payments', 'detail', id],
    queryFn: () => api.get<PendingPayment>(`/api/payments/${id}`),
    enabled: !!id,
  });
}

export interface VerifyPaymentDto {
  /** false = el admin ya descontó stock manualmente y NO quiere crear Movement.
   *  Default true (el backend crea Movement(salida) por cada item). */
  skipMovementCreate?: boolean;
}

export function useVerifyPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto?: VerifyPaymentDto }) =>
      api.patch<{
        ok: true;
        status: PendingPaymentStatus;
        oversellSkus?: Array<{ sku: string; requested: number; available: number }>;
      }>(`/api/payments/${id}/verify`, dto ?? {}),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pending-payments'] });
      qc.invalidateQueries({ queryKey: ['pending-payments', 'detail', vars.id] });
      // Verify dispara Movement(salida) → invalida inventario + dashboard.
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['movements'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export interface RejectPaymentDto {
  reason: PaymentRejectReason;
  note?: string;
}

export function useRejectPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: RejectPaymentDto }) =>
      api.patch<{ ok: true; status: PendingPaymentStatus }>(
        `/api/payments/${id}/reject`,
        dto,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pending-payments'] });
      qc.invalidateQueries({ queryKey: ['pending-payments', 'detail', vars.id] });
    },
  });
}

/** DTO opcional para reabrir un pago rechazado. `reason` es nota libre del
 *  admin sobre por qué cancela el rechazo (queda en el verification log para
 *  audit trail). Max 500 chars. */
export interface UnrejectPaymentDto {
  reason?: string;
}

/** PATCH /api/payments/:id/unreject — owner+admin. Mueve `status` de
 *  `rejected` de vuelta a `pending_verification` para que el admin pueda
 *  reconsiderar (ej. cliente envía mejor comprobante). NO restaura stock —
 *  el reject original no descontó nada. Sí persiste log `kind:'reopened'`. */
export function useUnrejectPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto?: UnrejectPaymentDto }) =>
      api.patch<{ ok: true; status: PendingPaymentStatus }>(
        `/api/payments/${id}/unreject`,
        dto ?? {},
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pending-payments'] });
      qc.invalidateQueries({ queryKey: ['pending-payments', 'detail', vars.id] });
    },
  });
}

/** PATCH /api/payments/:id/reopen — owner+admin. Deshace la VERIFICACIÓN:
 *  repone el stock debitado (contramovimiento), re-crea las reservas y
 *  devuelve el pago a pending_verification. Invalida inventario además de
 *  pagos porque la reposición mueve el kardex. */
export function useReopenPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<{ ok: true; status: PendingPaymentStatus }>(
        `/api/payments/${id}/reopen`,
        {},
      ),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['pending-payments'] });
      qc.invalidateQueries({ queryKey: ['pending-payments', 'detail', id] });
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['movements'] });
    },
  });
}

/** GET /api/payments/:id/share-link — link firmado del pedido (7 días) para
 *  compartir con el cliente: ver estado + SUBIR COMPROBANTE por la web. */
export function usePaymentShareLink() {
  return useMutation({
    mutationFn: (id: string) =>
      api.get<{ url: string; expiresInDays: number }>(
        `/api/payments/${id}/share-link`,
      ),
  });
}

/** POST /api/payments/:id/resend-email — owner+admin. Reenvía al cliente la
 *  notificación (email + chat) de un pago ya resuelto (verified/rejected).
 *  Útil para reenviar el resumen del pedido o probar la plantilla. No muta
 *  estado, así que no invalida queries. */
export function useResendPaymentEmail() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: true; status: PendingPaymentStatus; sentTo: string | null }>(
        `/api/payments/${id}/resend-email`,
        {},
      ),
  });
}

// ===== Payments F2-A — atribución + datos de envío =====
//
// El bot crea PendingPayment via API pública; en muchos flows captura los
// datos de envío en etapas o no captura atribución por timing. Estos hooks
// permiten al admin editar/llenar lo faltante desde `/payments`.
//
// TODO(F2-B): los endpoints privados PATCH /api/payments/:id/shipping y
// PATCH /api/payments/:id/attribution dependen de que F2-B los exponga con
// JwtAuthGuard + RolesGuard owner|admin. Mientras no existan, la mutation
// rebotará con 404 — la UI muestra el error inline.

export interface UpsertShippingDto {
  customerIdNumber?: string | null;
  customerEmail?: string | null;
  street?: string;
  streetNumber?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string;
  state?: string;
  postalCode?: string | null;
  country?: string;
  carrierPref?: string | null;
  notes?: string | null;
}

/** PATCH /api/payments/:id/shipping (privado, owner|admin). Crea o actualiza
 *  el ShippingInfo asociado al PendingPayment. Backend valida cédula CO,
 *  email, carrierPref enum. */
/**
 * Reenvía a mano el aviso de despacho. El automático sale una sola vez al
 * registrar la guía; esto cubre al comerciante que configuró la plantilla
 * después, o al cliente que dice que no le llegó.
 */
export function useNotifyShipped() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: true }>(`/api/payments/${id}/notify-shipped`, {}),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['pending-payments', 'detail', id] });
    },
  });
}

export function useUpdateShipping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpsertShippingDto }) =>
      api.patch<{ ok: true; shippingInfo: ShippingInfo }>(
        `/api/payments/${id}/shipping`,
        dto,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pending-payments', 'detail', vars.id] });
      qc.invalidateQueries({ queryKey: ['pending-payments'] });
    },
  });
}

export interface UpdateAttributionDto {
  originChannelKind?: OriginChannelKind | null;
  customerExternalId?: string | null;
  customerExternalIdKind?: string | null;
  ctwaClid?: string | null;
  ttclid?: string | null;
  gclid?: string | null;
  pclid?: string | null;
  adId?: string | null;
  campaignId?: string | null;
  adHeadline?: string | null;
  adBody?: string | null;
  adImageUrl?: string | null;
  referralSourceType?: string | null;
  referralSourceUrl?: string | null;
  referralRef?: string | null;
  referralExtras?: Record<string, unknown> | null;
}

/** PATCH /api/payments/:id/attribution (privado, owner|admin). Útil cuando
 *  el bot capturó el referral con delay respecto al primer mensaje. */
export function useUpdateAttribution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateAttributionDto }) =>
      api.patch<{ ok: true }>(`/api/payments/${id}/attribution`, dto),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['pending-payments', 'detail', vars.id] });
      qc.invalidateQueries({ queryKey: ['pending-payments'] });
    },
  });
}

// =====================================================================
// PriceLists / Pricing (F3-D)
// =====================================================================

export interface CreatePriceListDto {
  name: string;
  currencyCode: string;
  isDefault?: boolean;
}

export interface UpdatePriceListDto {
  name?: string;
  currencyCode?: string;
  isDefault?: boolean;
  active?: boolean;
}

export interface BulkSetPriceListItem {
  variantId: string;
  price: number | null;
}

/** Lista paginada (flat) de PriceLists del tenant. */
export function usePriceLists() {
  return useQuery({
    queryKey: ['price-lists'],
    queryFn: () => api.get<{ items: PriceList[] }>('/api/price-lists'),
  });
}

export function usePriceList(id: string | null | undefined) {
  return useQuery({
    queryKey: ['price-lists', 'detail', id],
    enabled: !!id,
    queryFn: () => api.get<PriceList>(`/api/price-lists/${id}`),
  });
}

export function usePriceListItems(id: string | null | undefined) {
  return useQuery({
    queryKey: ['price-list-items', id],
    enabled: !!id,
    queryFn: () =>
      api.get<{ items: PriceListItem[] }>(`/api/price-lists/${id}/items`),
  });
}

export function useCreatePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreatePriceListDto) =>
      api.post<PriceList>('/api/price-lists', dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['price-lists'] });
    },
  });
}

export function useUpdatePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdatePriceListDto }) =>
      api.patch<PriceList>(`/api/price-lists/${id}`, dto),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['price-lists'] });
      qc.invalidateQueries({ queryKey: ['price-lists', 'detail', vars.id] });
      // Cambiar el default afecta a otras listas (la previa pierde el flag).
      if (vars.dto.isDefault !== undefined) {
        qc.invalidateQueries({ queryKey: ['products'] });
      }
    },
  });
}

export function useDeletePriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.del<{ ok: true }>(`/api/price-lists/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['price-lists'] });
      qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

/**
 * PUT `/api/price-lists/:id/items/:variantId`. `price=null` borra el item
 * (la variant vuelve al fallback `Variant.price`). Si la lista es la default,
 * el backend sincroniza `Variant.price` con el nuevo valor — invalidamos
 * `products` para que el panel refleje el cambio.
 */
export function useSetPriceListItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      priceListId,
      variantId,
      price,
    }: {
      priceListId: string;
      variantId: string;
      price: number | null;
    }) =>
      api.put<{ ok: true; item: PriceListItem | null }>(
        `/api/price-lists/${priceListId}/items/${variantId}`,
        { price },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['price-list-items', vars.priceListId],
      });
      // Side-effect documentado en pricing.service: items en lista default
      // sincronizan Variant.price. Sin saber si esta lista es la default,
      // invalidamos productos para que el listado refresque (cheap).
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useBulkSetPriceListItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      priceListId,
      items,
    }: {
      priceListId: string;
      items: BulkSetPriceListItem[];
    }) =>
      api.post<{
        updated: number;
        deleted: number;
        errors: Array<{ variantId: string; reason: string }>;
      }>(`/api/price-lists/${priceListId}/items/bulk`, { items }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['price-list-items', vars.priceListId],
      });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Body para `POST /api/price-lists/:listId/bulk-fill` (V3.3.2 wave 2).
 *
 * Tres modos (espejo del DTO backend):
 *   - `fill_empty` (default): copia `fromListId × factor`, no toca existentes.
 *   - `overwrite`: copia `fromListId × factor`, sobrescribe.
 *   - `fixed_price`: ignora `fromListId`/`factor`, setea `fixedPrice` en
 *     TODOS los variants activos del tenant. Atajo "todo a $25 USD".
 *
 * Cross-field validation vive en el service: para `fixed_price` requiere
 * `fixedPrice`; para los otros modos requiere `fromListId + factor`. Por eso
 * los tres campos son opcionales aquí — el caller arma el shape correcto.
 *
 * Bounds runtime backend: `factor` ∈ [1e-6, 1e6], `fixedPrice` > 0 y finito.
 */
export interface BulkFillPriceListDto {
  fromListId?: string;
  factor?: number;
  fixedPrice?: number;
  mode?: 'fill_empty' | 'overwrite' | 'fixed_price';
}

export function useBulkFillPriceList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      dto,
    }: {
      listId: string;
      dto: BulkFillPriceListDto;
    }) =>
      api.post<{ created: number; updated: number; skipped: number }>(
        `/api/price-lists/${listId}/bulk-fill`,
        dto,
      ),
    onSuccess: (_data, vars) => {
      // Items del destino cambiaron → refetch grid.
      qc.invalidateQueries({
        queryKey: ['price-list-items', vars.listId],
      });
      // Si el destino es el default del tenant, el backend sincroniza
      // Variant.price (mismo side-effect que setItem/bulkSetItems). Sin
      // saberlo acá, invalidamos productos para que el listado refresque.
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// =====================================================================
// Prices Grid — PR-2 (página /price-lists?tab=grid editable multi-producto)
// =====================================================================
//
// Espejo del PricingService.getGrid/bulkEditGrid/previewImportDiff/applyImportDiff.
// Ver apps/api/src/modules/pricing/pricing.service.ts §PR-1 para la semántica.
// Side-effect importante (líneas 1474-1488 del service): cuando un diff toca
// la lista default, el backend sincroniza `Variant.price`. Por eso TODAS las
// mutations de grid invalidan también `['products']` y `['price-list-items']`.

export interface GridQueryInput {
  q?: string;
  categoryId?: string;
  priceListIds?: string[];
  onlyMissing?: boolean;
  page?: number;
  pageSize?: number;
  sort?: 'sku_asc' | 'sku_desc' | 'name_asc' | 'name_desc';
}

export interface GridPriceCell {
  priceListId: string;
  priceListName: string;
  currencyCode: string;
  isDefault: boolean;
  price: number | null;
  isLegacyFallback: boolean;
}

export interface GridRow {
  productId: string;
  productSku: string;
  productName: string;
  categorySlug: string | null;
  variantId: string;
  variantSku: string;
  variantLabel: string | null;
  attributes: Record<string, string>;
  prices: GridPriceCell[];
}

export interface GridPriceListMeta {
  id: string;
  name: string;
  currencyCode: string;
  isDefault: boolean;
  active: boolean;
}

export interface GridResponse {
  rows: GridRow[];
  priceLists: GridPriceListMeta[];
  total: number;
  completeness: {
    totalVariants: number;
    completeByList: Record<string, number>;
    completenessPercentByList: Record<string, number>;
  };
}

export interface GridBulkDiff {
  variantId: string;
  priceListId: string;
  price: number | null;
}

export interface GridBulkEditResult {
  updated: number;
  created: number;
  deleted: number;
  skipped: number;
  errors: Array<{ variantId: string; priceListId: string; reason: string }>;
}

export interface GridImportDiffRow {
  variantId: string;
  variantSku: string;
  priceListId: string;
  priceListName: string;
  currentPrice: number | null;
  newPrice: number | null;
  action: 'create' | 'update' | 'delete' | 'unchanged' | 'error';
}

export interface GridImportDiffError {
  row: number;
  reason: string;
  raw: Record<string, unknown>;
}

export interface GridImportDiffPreview {
  previewToken: string;
  expiresAt: string;
  summary: {
    totalRows: number;
    creates: number;
    updates: number;
    deletes: number;
    unchanged: number;
    errors: number;
  };
  errors: GridImportDiffError[];
  plan: GridImportDiffRow[];
}

/** Serializa GridQueryInput para query string. Omite undefined/empty. */
function serializeGridQuery(input: GridQueryInput): string {
  const params = new URLSearchParams();
  if (input.q !== undefined && input.q !== '') params.set('q', input.q);
  if (input.categoryId) params.set('categoryId', input.categoryId);
  if (input.priceListIds && input.priceListIds.length > 0) {
    // El backend acepta CSV o array; CSV es más compacto en URL.
    params.set('priceListIds', input.priceListIds.join(','));
  }
  if (input.onlyMissing) params.set('onlyMissing', 'true');
  if (input.page !== undefined) params.set('page', String(input.page));
  if (input.pageSize !== undefined) params.set('pageSize', String(input.pageSize));
  if (input.sort) params.set('sort', input.sort);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useGridPrices(query: GridQueryInput, enabled = true) {
  return useQuery({
    queryKey: ['prices-grid', query],
    enabled,
    queryFn: () =>
      api.get<GridResponse>(`/api/prices/grid${serializeGridQuery(query)}`),
    // Cache corto: el grid cambia con cada bulk-edit; mantener un buffer
    // breve evita refetch al re-montar la misma página por navegación interna.
    staleTime: 5_000,
  });
}

export function useBulkEditGrid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (diffs: GridBulkDiff[]) =>
      api.post<GridBulkEditResult>('/api/prices/grid/bulk-edit', { diffs }),
    onSuccess: () => {
      // El grid del propio panel.
      qc.invalidateQueries({ queryKey: ['prices-grid'] });
      // Items per-list que pueda estar viendo el ProductDrawer abierto.
      qc.invalidateQueries({ queryKey: ['price-list-items'] });
      // Variant.price legacy sincronizado por el backend cuando el diff toca
      // la lista default. Refetch products para que listings reflejen el cambio.
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useImportDiff() {
  return useMutation({
    mutationFn: (file: File) =>
      api.upload<GridImportDiffPreview>(
        '/api/prices/grid/import-diff',
        file,
        'file',
      ),
  });
}

export function useApplyImportDiff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (previewToken: string) =>
      api.post<GridBulkEditResult>('/api/prices/grid/import-diff/apply', {
        previewToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prices-grid'] });
      qc.invalidateQueries({ queryKey: ['price-list-items'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// =====================================================================
// Channels — PATCH (F3-D: setear priceListId)
// =====================================================================

export interface UpdateChannelDto {
  name?: string;
  /** `null` limpia la asociación → canal usa default del tenant. */
  priceListId?: string | null;
}

export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateChannelDto }) =>
      api.patch<{ id: string }>(`/api/channels/${id}`, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

// ===== Webhooks outbound (F5-E) =====

/**
 * Lista de webhook endpoints del tenant. Endpoint owner/admin gestiona y
 * member sólo lee (RolesGuard backend valida). El secret nunca viene acá —
 * solo en POST /api/webhooks como `plaintextSecret`.
 */
export function useWebhookEndpoints() {
  return useQuery({
    queryKey: ['webhook-endpoints'],
    queryFn: () =>
      api.get<{ items: WebhookEndpointType[] }>('/api/webhooks'),
  });
}

export function useWebhookEndpoint(id: string | null) {
  return useQuery({
    queryKey: ['webhook-endpoints', id],
    queryFn: () => api.get<WebhookEndpointType>(`/api/webhooks/${id}`),
    enabled: !!id,
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      name: string;
      url: string;
      events: WebhookEventType[];
    }) => api.post<WebhookEndpointCreated>('/api/webhooks', vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhook-endpoints'] }),
  });
}

export function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: {
        name?: string;
        url?: string;
        events?: WebhookEventType[];
        active?: boolean;
      };
    }) => api.patch<WebhookEndpointType>(`/api/webhooks/${id}`, dto),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['webhook-endpoints'] });
      qc.invalidateQueries({ queryKey: ['webhook-endpoints', vars.id] });
    },
  });
}

/**
 * Borrado físico. ADR-005: para "pausar" sin perder historial, usar
 * useUpdateWebhook con { active: false }. El backend cascadea las
 * deliveries históricas del endpoint borrado.
 */
export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        await api.del<void>(`/api/webhooks/${id}`);
        return { ok: true as const, alreadyMissing: false as const };
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { ok: true as const, alreadyMissing: true as const };
        }
        throw err;
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['webhook-endpoints'] }),
  });
}

/**
 * Historial de deliveries para un endpoint. Filtra por status opcional.
 * Limit default 50, máximo 200 (backend valida).
 */
export function useWebhookDeliveries(
  endpointId: string | null,
  filters: { status?: DeliveryStatus; limit?: number } = {},
) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return useQuery({
    queryKey: ['webhook-deliveries', endpointId, filters],
    queryFn: () =>
      api.get<{ items: WebhookDeliveryType[] }>(
        `/api/webhooks/${endpointId}/deliveries${qs ? `?${qs}` : ''}`,
      ),
    enabled: !!endpointId,
  });
}

/**
 * Reintento manual de una delivery (failed/dead_letter). Resetea
 * nextAttemptAt=now() — el worker la levanta al siguiente tick.
 */
export function useRetryDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      endpointId,
      deliveryId,
    }: {
      endpointId: string;
      deliveryId: string;
    }) =>
      api.post<WebhookDeliveryType>(
        `/api/webhooks/${endpointId}/deliveries/${deliveryId}/retry`,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['webhook-deliveries', vars.endpointId],
      });
    },
  });
}

/**
 * Dispara un evento de prueba (`webhook.test`) al endpoint. Útil para que
 * el merchant valide su receiver con HMAC + headers correctos.
 */
export function useSendTestEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (endpointId: string) =>
      api.post<WebhookDeliveryType>(`/api/webhooks/${endpointId}/test`),
    onSuccess: (_data, endpointId) => {
      qc.invalidateQueries({ queryKey: ['webhook-deliveries', endpointId] });
    },
  });
}

// ===== Atribución de conversiones (F6) =====
//
// Plataformas de ads + eventos despachados + reporting agregado por ad.
// CRUD restringido a owner/admin (RolesGuard backend); reporting accesible a
// todos los roles.

export function useConversionPlatforms() {
  return useQuery({
    queryKey: ['conversion-platforms'],
    queryFn: () =>
      api.get<{ items: ConversionPlatformType[] }>('/api/conversion-platforms'),
  });
}

export function useConversionPlatform(id: string | null) {
  return useQuery({
    queryKey: ['conversion-platforms', id],
    queryFn: () =>
      api.get<ConversionPlatformType>(`/api/conversion-platforms/${id}`),
    enabled: !!id,
  });
}

export function useCreateConversionPlatform() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      platform: ConversionPlatformKind;
      pixelId?: string;
      accessToken: string;
      testEventCode?: string;
    }) =>
      api.post<ConversionPlatformType>('/api/conversion-platforms', vars),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['conversion-platforms'] }),
  });
}

export function useUpdateConversionPlatform() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: {
        pixelId?: string;
        accessToken?: string;
        testEventCode?: string | null;
        active?: boolean;
      };
    }) =>
      api.patch<ConversionPlatformType>(
        `/api/conversion-platforms/${id}`,
        dto,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['conversion-platforms'] });
      qc.invalidateQueries({ queryKey: ['conversion-platforms', vars.id] });
    },
  });
}

export function useDeleteConversionPlatform() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        await api.del<void>(`/api/conversion-platforms/${id}`);
        return { ok: true as const, alreadyMissing: false as const };
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { ok: true as const, alreadyMissing: true as const };
        }
        throw err;
      }
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ['conversion-platforms'] }),
  });
}

/**
 * Historial de events despachados a una plataforma. Filtra por status
 * (pending/sent/failed). Limit default 50, máximo 200.
 */
export function useConversionEvents(
  platformId: string | null,
  filters: { status?: ConversionEventStatus; limit?: number } = {},
) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return useQuery({
    queryKey: ['conversion-events', platformId, filters],
    queryFn: () =>
      api.get<{ items: ConversionEventType[] }>(
        `/api/conversion-platforms/${platformId}/events${qs ? `?${qs}` : ''}`,
      ),
    enabled: !!platformId,
  });
}

/**
 * Reintenta un event `failed`. Resetea status=pending y attempts=0; el
 * worker lo levanta en el próximo tick.
 */
export function useRetryConversionEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      platformId,
      eventId,
    }: {
      platformId: string;
      eventId: string;
    }) =>
      api.post<ConversionEventType>(
        `/api/conversion-platforms/${platformId}/events/${eventId}/retry`,
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['conversion-events', vars.platformId],
      });
    },
  });
}

/**
 * Reporte de atribución agrupado por ad. `from` y `to` son ISO 8601.
 * Filtros opcionales originChannel + platform reducen el conjunto.
 */
export function useAttributionReport(filters: {
  from: string;
  to: string;
  originChannel?: string;
  platform?: ConversionPlatformKind;
}) {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  if (filters.originChannel) params.set('originChannel', filters.originChannel);
  if (filters.platform) params.set('platform', filters.platform);
  return useQuery({
    queryKey: ['attribution-report', filters],
    queryFn: () =>
      api.get<AttributionReportResponse>(
        `/api/reports/attribution?${params.toString()}`,
      ),
    enabled: !!filters.from && !!filters.to,
  });
}

/**
 * URL del export CSV. NO usa useQuery — el browser navega a la URL para
 * desencadenar la descarga. Las cookies del JWT se mandan automático.
 */
export function buildAttributionCsvUrl(filters: {
  from: string;
  to: string;
  originChannel?: string;
  platform?: ConversionPlatformKind;
}): string {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  if (filters.originChannel) params.set('originChannel', filters.originChannel);
  if (filters.platform) params.set('platform', filters.platform);
  return `${API_BASE}/api/reports/attribution.csv?${params.toString()}`;
}

// ===== Dashboard extended (F7-E) =====

/** Shape de `/api/dashboard/stats` (KPI básico) — usado por la home. */
export interface DashboardStats {
  totalActiveProducts: number;
  totalActiveVariants: number;
  totalQty: number;
  inventoryValue: number;
  /** Variants TRACKEADAS con total_qty <= 0. NO incluye variants sin
   *  inventory_levels (esas se exponen en `untrackedVariants`). */
  outOfStockSkus: number;
  lowStockSkus: number;
  /** Variants activas sin ningún inventory_level — el merchant nunca
   *  configuró tracking. Se muestran en card separada con CTA a
   *  /reorder-points en lugar de inflar el contador de "stock bajo". */
  untrackedVariants: number;
  topLowStock: Array<{
    productId: string;
    sku: string;
    name: string;
    totalQty: number;
    reorderPoint: number | null;
  }>;
}

/** Snapshot del estado de configuración del tenant.
 *  Lo consume la card "Estado del módulo" del Dashboard (reemplazó al
 *  bloque "Roadmap" hard-coded). Backend lo construye en `/api/dashboard/extended`.
 *  Opcional para mantener backward-compat con versiones de backend que
 *  todavía no lo emiten — el frontend oculta la card si viene undefined.
 *
 *  Sub-secciones:
 *   - channels: integraciones conectadas (Shopify, Whatsfy E-com, etc).
 *   - paymentMethods: métodos de pago activos (Bancolombia, Bre-B, etc).
 *   - priceLists: cuántas listas + moneda del default.
 *   - conversionPlatforms: Meta CAPI / TikTok / Google Ads / Pinterest.
 *   - webhooks: endpoints outbound activos.
 *   - apiKeys: API keys públicas activas.
 *   - botReady: heurística — el bot Whatsfy puede operar si hay 1+ API
 *     key con scopes mínimos + 1+ canal conectado. NO valida que Andrés
 *     haya pegado la key en Custom Fields (no es observable desde acá). */
export interface ModuleStatus {
  channels: Array<{
    id: string;
    kind: string;
    name: string;
    status: string | null;
    connected: boolean;
  }>;
  paymentMethods: {
    active: number;
    types: string[];
  };
  priceLists: {
    count: number;
    defaultCurrency: string;
  };
  conversionPlatforms: Array<{
    platform: 'meta' | 'tiktok' | 'google' | 'pinterest';
    active: boolean;
    hasToken: boolean;
  }>;
  webhooks: {
    active: number;
  };
  apiKeys: {
    active: number;
  };
  botReady: {
    ready: boolean;
    /** Lista de gaps cuando `ready=false`: 'api_key' | 'channel' */
    missing: string[];
  };
}

/** Shape de `/api/dashboard/extended` (analytics) — F7-E.
 *  Reusa `stats` (KPI básico) + agrega salesByChannel, trend 30d, topAds
 *  (reuso de F6 reporting), y lowStock extendido (cap 50). */
export interface DashboardExtended {
  stats: DashboardStats;
  salesByChannel: Array<{
    /** `'unknown'` cuando `origin_channel_kind` en DB era NULL. */
    originChannelKind: OriginChannelKind | 'unknown';
    currency: string;
    count: number;
    totalValue: number;
  }>;
  trend: Array<{
    /** ISO date `yyyy-MM-dd`. */
    date: string;
    verifiedCount: number;
    totalValue: number;
  }>;
  topAds: Array<{
    adId: string;
    adHeadline: string | null;
    campaignId: string | null;
    verifiedCount: number;
    totalValue: number;
    currency: string;
  }>;
  lowStock: Array<{
    variantId: string;
    productId: string;
    productSku: string;
    sku: string;
    name: string;
    totalQty: number;
    reorderPoint: number | null;
  }>;
  /** Opcional — backend emite este campo en versiones nuevas (DASH-B).
   *  Si está ausente, la card "Estado del módulo" se oculta. */
  moduleStatus?: ModuleStatus;
}

export function useDashboardExtended() {
  return useQuery({
    queryKey: ['dashboard', 'extended'],
    queryFn: () => api.get<DashboardExtended>('/api/dashboard/extended'),
  });
}

/**
 * V3.2 SA3: muta `slug` y/o `landingConfig` del tenant. Endpoint user-facing
 * scoped al storefront público (separado de `/auth/tenant-settings` que cubre
 * locale/branding del módulo). Solo owner/admin — role gate inline en el
 * backend; el frontend ya bloquea la ruta `/configuracion` para member en
 * Layout.
 *
 * Convención de URLs en este módulo: los endpoints bajo `/auth/*` NO llevan
 * prefijo `/api` (ver `useUpdateTenantSettings`). El handler responde el
 * tenant actualizado para que el caller invalide `['tenant-settings']` con
 * data fresca y el form re-popule sin requerir refetch redundante.
 */
export function useUpdateTenantStorefront() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateTenantStorefrontPayload) =>
      api.patch<{
        ok: true;
        tenant: { id: string; slug: string | null };
        config: ResolvedTenantConfig;
      }>('/auth/tenant-settings/storefront', dto),
    onSuccess: () =>
      // brand.* del config + slug pueden afectar al header del Layout (que
      // lee /me) — invalidar ambos. Se espera el refetch para que quien
      // guarda (el logo, la tienda) vea el dato nuevo sin parpadeo.
      Promise.all([
        qc.invalidateQueries({ queryKey: ['tenant-settings'] }),
        qc.invalidateQueries({ queryKey: ['me'] }),
      ]),
  });
}

/**
 * V3.2 SA4: muta SOLO el `deeplinkConfig` de un Channel. Endpoint dedicado en
 * paralelo con `PATCH /api/channels/:id` (que también acepta `name` /
 * `priceListId`) — el path acotado da auditoría granular
 * (`action='updateDeeplinkConfig'`) sin que la UI tenga que mandar el resto
 * de campos.
 *
 * Replace semantics: el objeto completo reemplaza el JSONB persistido. La UI
 * debe armar el `deeplinkConfig` final (omitiendo claves vacías) antes de
 * mandar — el backend valida cada campo con regex estricto (E.164 sin `+`,
 * usernames sin `@`, URL https). Solo owner/admin (`RolesGuard` backend).
 */
export function useUpdateChannelDeeplinkConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      deeplinkConfig,
    }: {
      id: string;
      deeplinkConfig: ChannelDeeplinkConfig;
    }) =>
      api.patch<Channel>(`/api/channels/${id}/deeplink-config`, {
        deeplinkConfig,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

// ===== Volume discounts =====
//
// Descuentos por volumen (Sprint V3.4): el comerciante define umbrales de
// cantidad por SKU/variante/tenant-wide y el precio se reduce automáticamente
// cuando el carrito alcanza el `minQty`. Tres scopes:
//   - tenant-wide (`productId=null, variantId=null`): aplica a TODO el catálogo.
//   - product-level (`productId=<uuid>, variantId=null`): aplica a todas las
//     variantes del producto.
//   - variant-level (`productId=<uuid>, variantId=<uuid>`): aplica solo a una
//     variante específica (color/talla puntual).
//
// `kind`:
//   - `percent`: `value` es % (0-100), se aplica como reducción al precio
//     unitario.
//   - `fixed`: `value` es monto en la moneda del tenant, se resta del precio
//     unitario.
//
// Backend `/api/volume-discounts` (CRUD owner/admin) lo provee SA-VOLUME-SCHEMA.
// Si el endpoint aún no está montado, la query devuelve 404 — la UI muestra
// empty state. Las mutations rebotan con error inline.

export type VolumeDiscountKind = 'percent' | 'fixed';

export interface VolumeDiscount {
  id: string;
  /** null = tenant-wide o variant-level (verificar variantId). */
  productId: string | null;
  /** null = scope a nivel producto o tenant. */
  variantId: string | null;
  /** Mínimo de cantidad para que el descuento aplique. Entero >= 1. */
  minQty: number;
  kind: VolumeDiscountKind;
  /** Decimal serializado como string (preserva precisión). 0-100 si
   *  kind=percent; monto en currency del tenant si kind=fixed. */
  value: string;
  /** Etiqueta opcional para mostrar en el carrito ("3x2", "Combo familiar"). */
  label: string | null;
  active: boolean;
  /** Denormalizados por el backend para evitar lookups N+1 desde el front. */
  product?: { id: string; name: string; sku: string } | null;
  variant?: { id: string; sku: string; label: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVolumeDiscountDto {
  productId?: string | null;
  variantId?: string | null;
  minQty: number;
  kind: VolumeDiscountKind;
  value: number;
  label?: string | null;
  active?: boolean;
}

export interface UpdateVolumeDiscountDto {
  minQty?: number;
  kind?: VolumeDiscountKind;
  value?: number;
  label?: string | null;
  active?: boolean;
}

/**
 * Lista de descuentos por volumen del tenant. Si el endpoint aún no existe
 * (SA-VOLUME-SCHEMA en deploy), el catch devuelve `{ items: [] }` para que la
 * UI no rompa — empty state hasta que el backend esté arriba.
 */
export function useVolumeDiscounts() {
  return useQuery({
    queryKey: ['volume-discounts'],
    queryFn: async () => {
      try {
        return await api.get<{ items: VolumeDiscount[] }>(
          '/api/volume-discounts',
        );
      } catch (err) {
        // Backend aún no desplegado: devolvemos shape vacío. Cualquier otro
        // error sí burbujea para que la UI lo muestre.
        if (err instanceof ApiError && err.status === 404) {
          return { items: [] };
        }
        throw err;
      }
    },
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiError && error.status === 401),
  });
}

export function useCreateVolumeDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateVolumeDiscountDto) =>
      api.post<VolumeDiscount>('/api/volume-discounts', dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['volume-discounts'] });
    },
  });
}

export function useUpdateVolumeDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateVolumeDiscountDto }) =>
      api.patch<VolumeDiscount>(`/api/volume-discounts/${id}`, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['volume-discounts'] });
    },
  });
}

/**
 * Archiva un descuento (soft-delete: setea `active:false`). Conserva el id
 * para que las referencias históricas en audit log queden navegables. Para
 * re-activar usar `useUpdateVolumeDiscount` con `{ active: true }`.
 */
export function useDeleteVolumeDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/volume-discounts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['volume-discounts'] });
    },
  });
}


